/**
 * 🔄 Versiyon Kontrol Hook'u
 *
 * PWA sessiz güncelleme (autoUpdate) sonrası kullanıcıyı bilgilendirir.
 *
 * Mekanizma:
 * 1. SW "controllerchange" anında yeni sürümü yakalar.
 * 2. Postacı'ya (Bildirimler tablosu) "system_update" bildirimi ekler (Sessiz).
 * 3. Eğer sistem izni varsa NATIVE (sistem çekmecesi) bildirimi tetikler.
 * 4. 2 saniye sonra sayfayı tazeleyerek yeni sürümü aktif eder.
 */
import { useEffect, useRef } from 'react'
import { registerSW } from 'virtual:pwa-register'
import { supabase } from '@/lib/supabase'
import { logError } from '@/lib/log'

export function useVersionCheck() {
  const hasChecked = useRef(false)

  useEffect(() => {
    // StrictMode çift çalışmasını engelle
    if (hasChecked.current) return
    hasChecked.current = true

    // 1. PWA Servis İşçisini kaydet ve periyodik kontrol başlat
    const updateSW = registerSW({
      onNeedRefresh() {
        // Yeni bir versiyon bulunduğunda hemen skipWaiting tetikle
        updateSW(true)
      },
      onRegistered(r) {
        if (r) {
          // Her saat başı yeni sürüm var mı arka planda kontrol et
          setInterval(
            () => {
              r.update()
            },
            60 * 60 * 1000
          )
        }
      },
      onOfflineReady() {
        // PWA cevrimdisi kullanima hazir; kullaniciya gosterilecek bir sey yok.
      },
    })

    // 2. autoUpdate stratejisinde yeni SW yüklenip aktifleştiğinde controllerchange tetiklenir.
    let refreshing = false
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', async () => {
        if (refreshing) return
        refreshing = true

        // 🛡️ Bildirim akışı devam ediyorsa reload'u ertele
        if (sessionStorage.getItem('gv_notif_flow_active') === 'true') {
          // Akış bitene kadar 500ms aralıklarla kontrol et, max 30sn
          let waited = 0
          while (sessionStorage.getItem('gv_notif_flow_active') === 'true' && waited < 30000) {
            await new Promise(r => setTimeout(r, 500))
            waited += 500
          }
        }

        const currentVersion = import.meta.env.VITE_APP_VERSION || '2.5.3'

        // 1. Kullanıcı tercihlerini kontrol et
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser()
          if (user) {
            // Tercihleri getir
            const { data: prefsData } = await supabase
              .from('user_preferences')
              .select('preferences')
              .eq('user_id', user.id)
              .maybeSingle()

            const prefs = (prefsData?.preferences as any) || {}
            const canNotify = prefs.push_notifications !== false && prefs.system_update !== false

            // 2. Eğer izin varsa ve kullanıcı istiyorsa NATIVE (sistem) bildirimi göster
            if (canNotify && 'Notification' in window && Notification.permission === 'granted') {
              new Notification('✨ Günüm Var Güncellendi', {
                body: `v${currentVersion} sürümü yayında. Yeni özellikler ve iyileştirmeler eklendi.`,
                icon: '/favicon.ico',
              })
            }
          }
        } catch (e) {
          logError('Versiyon bildirimi oluşturulamadı:', e)
        }

        // 4. 1 saniye sonra sayfayı zorla yenile
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      })
    }
  }, [])
}
